import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SingleSelectOption } from "@/components/controls/SingleSelect";
import { useProducts } from "@/entities/settings/hooks";
import { useDeletedServiceItems } from "@/pages/settings/pages/products-v2/hooks";
import { STRIPE_CHECKOUT_PREFIX } from "@/pages/settings/components/integrations/stripeCheckout/stripeCheckoutOptions";

const PREFIX = `${STRIPE_CHECKOUT_PREFIX}.prices`;

// Every live service item of the catalog, named with its product, plus any
// saved one the catalog no longer lists, so the admin can see what to repoint.
export const useServiceItemOptions = (
  productId: string | null,
  savedIds: string[]
) => {
  const { t } = useTranslation();
  const productsQuery = useProducts();
  const products = useMemo(
    () => productsQuery.data?.products ?? [],
    [productsQuery.data]
  );

  const catalogOptions = useMemo<SingleSelectOption[]>(
    () =>
      products.flatMap((product) =>
        product.serviceItems
          .filter((item) => !item.isDeleted)
          .map((item) => ({
            id: item.id,
            displayName: `${product.name} · ${item.name}`
          }))
      ),
    [products]
  );

  const missingIds = useMemo(() => {
    if (!productsQuery.isSuccess) return [];
    const known = new Set(catalogOptions.map((option) => option.id));
    return [...new Set(savedIds)].filter((id) => id && !known.has(id));
  }, [catalogOptions, productsQuery.isSuccess, savedIds]);

  // A removed item is looked up under the mapping's product line, the only
  // product the settings name.
  const deletedItems = useDeletedServiceItems(
    productId ?? undefined,
    missingIds
  );

  const options = useMemo<SingleSelectOption[]>(() => {
    const missing = missingIds.map((id) => {
      const deleted = deletedItems.find((item) => item.id === id);
      return {
        id,
        displayName: deleted
          ? t(`${PREFIX}.deletedItem`, { name: deleted.name })
          : t(`${PREFIX}.notInCatalog`)
      };
    });
    return [...catalogOptions, ...missing];
  }, [catalogOptions, deletedItems, missingIds, t]);

  const nameOf = (id: string | null) =>
    id ? (options.find((option) => option.id === id)?.displayName ?? "") : "";

  return {
    options,
    nameOf,
    isLoading: productsQuery.isLoading,
    isError: productsQuery.isError,
    products
  };
};
